/**
 * Custom Asset Creation Component
 * Flexible workflow for custom project types that don't fit standard templates
 */

import React, { useState, useEffect } from 'react';
import { Wrench, Upload, Eye, Send, ChevronLeft, ChevronRight, Plus, X, Save } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface CustomAssetProps {
  clientId?: number;
  planId?: number;
  projectId?: number;
  onComplete?: (contentId: number) => void;
  onCancel?: () => void;
}

interface WorkflowStep {
  id: string;
  order: number;
  name: string;
  type: 'metadata' | 'content' | 'assets' | 'preview' | 'approval' | 'custom';
  required: boolean;
  data: any;
}

interface CustomField {
  id: string;
  name: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'checkbox' | 'file';
  value: any;
  options?: string[];
  required?: boolean;
}

export default function CustomAssetCreation({ clientId, planId, projectId, onComplete, onCancel }: CustomAssetProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([
    { id: '1', order: 1, name: 'Project Details', type: 'metadata', required: true, data: {} },
    { id: '2', order: 2, name: 'Content Creation', type: 'content', required: true, data: {} },
    { id: '3', order: 3, name: 'Asset Upload', type: 'assets', required: false, data: {} },
    { id: '4', order: 4, name: 'Preview', type: 'preview', required: false, data: {} },
    { id: '5', order: 5, name: 'Submit for Approval', type: 'approval', required: true, data: {} },
  ]);

  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [contentData, setContentData] = useState({
    projectName: '',
    projectType: '',
    description: '',
    priority: 'medium',
    dueDate: '',
  });
  const [mainContent, setMainContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const addCustomField = () => {
    setCustomFields([
      ...customFields,
      {
        id: Date.now().toString(),
        name: '',
        type: 'text',
        value: '',
        required: false,
      }
    ]);
  };

  const removeCustomField = (id: string) => {
    setCustomFields(customFields.filter(f => f.id !== id));
  };

  const updateCustomField = (id: string, updates: Partial<CustomField>) => {
    setCustomFields(customFields.map(f =>
      f.id === id ? { ...f, ...updates } : f
    ));
  };

  const handleFilesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const addWorkflowStep = () => {
    const newStep: WorkflowStep = {
      id: Date.now().toString(),
      order: workflowSteps.length + 1,
      name: `Step ${workflowSteps.length + 1}`,
      type: 'custom',
      required: false,
      data: {},
    };
    setWorkflowSteps([...workflowSteps, newStep]);
  };

  const removeWorkflowStep = (id: string) => {
    const filteredSteps = workflowSteps.filter(s => s.id !== id);
    // Reorder remaining steps
    const reorderedSteps = filteredSteps.map((step, index) => ({
      ...step,
      order: index + 1,
    }));
    setWorkflowSteps(reorderedSteps);
  };

  const updateWorkflowStep = (id: string, updates: Partial<WorkflowStep>) => {
    setWorkflowSteps(workflowSteps.map(s =>
      s.id === id ? { ...s, ...updates } : s
    ));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Build custom fields data
      const customFieldsData = customFields.reduce((acc, field) => {
        acc[field.name] = field.value;
        return acc;
      }, {} as Record<string, any>);

      // Step 1: Create content item
      const contentPayload = {
        type: 'custom',
        content_subtype: contentData.projectType || 'custom_project',
        title: contentData.projectName,
        description: contentData.description,
        status: 'draft',
        client_id: clientId,
        plan_id: planId,
        project_id: projectId,
        platform_config: JSON.stringify({
          project_type: contentData.projectType,
          priority: contentData.priority,
          due_date: contentData.dueDate,
          main_content: mainContent,
          custom_fields: customFieldsData,
          workflow_definition: workflowSteps,
        }),
      };

      const contentRes = await axios.post(`${API_BASE_URL}/content`, contentPayload);
      const contentId = contentRes.data.id;

      // Step 2: Upload files
      if (files.length > 0) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('content_id', contentId.toString());

          // Determine asset type from file
          let assetType = 'document';
          if (file.type.startsWith('image/')) assetType = 'image';
          else if (file.type.startsWith('video/')) assetType = 'video';
          else if (file.type.startsWith('audio/')) assetType = 'audio';

          formData.append('asset_type', assetType);
          formData.append('asset_group', 'supplementary');

          await axios.post(`${API_BASE_URL}/assets/upload`, formData);
        }
      }

      // Step 3: Create workflow steps
      for (const step of workflowSteps) {
        await axios.post(`${API_BASE_URL}/content/${contentId}/workflow/steps`, {
          step_order: step.order,
          step_name: step.name,
          step_type: step.type,
          status: step.order <= currentStep ? 'completed' : 'pending',
          step_data: JSON.stringify(step.data),
        });
      }

      alert('Custom project created successfully!');
      if (onComplete) onComplete(contentId);
    } catch (error) {
      console.error('Failed to create custom asset:', error);
      alert('Failed to create custom project');
    } finally {
      setLoading(false);
    }
  };

  const renderFieldInput = (field: CustomField) => {
    const commonProps = {
      className: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500",
      required: field.required,
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            value={field.value}
            onChange={(e) => updateCustomField(field.id, { value: e.target.value })}
            rows={3}
          />
        );
      case 'number':
        return (
          <input
            {...commonProps}
            type="number"
            value={field.value}
            onChange={(e) => updateCustomField(field.id, { value: e.target.value })}
          />
        );
      case 'date':
        return (
          <input
            {...commonProps}
            type="date"
            value={field.value}
            onChange={(e) => updateCustomField(field.id, { value: e.target.value })}
          />
        );
      case 'select':
        return (
          <select
            {...commonProps}
            value={field.value}
            onChange={(e) => updateCustomField(field.id, { value: e.target.value })}
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={field.value}
            onChange={(e) => updateCustomField(field.id, { value: e.target.checked })}
            className="w-4 h-4"
          />
        );
      default:
        return (
          <input
            {...commonProps}
            type="text"
            value={field.value}
            onChange={(e) => updateCustomField(field.id, { value: e.target.value })}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Wrench className="w-6 h-6 text-blue-600" />
                Create Custom Project
              </h1>
              <p className="text-gray-600 mt-1">Flexible workflow for unique project types</p>
            </div>
            {onCancel && (
              <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            )}
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6 overflow-x-auto">
            {workflowSteps.slice(0, 5).map((step, i) => (
              <React.Fragment key={step.id}>
                <div className={`flex items-center ${currentStep >= step.order ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.order ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step.order}
                  </div>
                  <span className="ml-2 text-sm font-medium hidden sm:inline truncate max-w-[100px]">{step.name}</span>
                </div>
                {i < 4 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Step 1: Project Details + Custom Fields */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Project Details</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                <input
                  type="text"
                  value={contentData.projectName}
                  onChange={(e) => setContentData({ ...contentData, projectName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Custom Website Integration"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Type</label>
                  <input
                    type="text"
                    value={contentData.projectType}
                    onChange={(e) => setContentData({ ...contentData, projectType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Integration, Migration, Custom Build"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={contentData.priority}
                    onChange={(e) => setContentData({ ...contentData, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={contentData.description}
                  onChange={(e) => setContentData({ ...contentData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the project objectives and requirements"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={contentData.dueDate}
                  onChange={(e) => setContentData({ ...contentData, dueDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Custom Fields Builder */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Custom Fields</h3>
                  <button
                    onClick={addCustomField}
                    className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Field
                  </button>
                </div>

                {customFields.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No custom fields added yet</p>
                )}

                <div className="space-y-3">
                  {customFields.map((field) => (
                    <div key={field.id} className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-4">
                          <input
                            type="text"
                            value={field.name}
                            onChange={(e) => updateCustomField(field.id, { name: e.target.value })}
                            placeholder="Field name"
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <select
                            value={field.type}
                            onChange={(e) => updateCustomField(field.id, { type: e.target.value as any })}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          >
                            <option value="text">Text</option>
                            <option value="textarea">Textarea</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="select">Select</option>
                            <option value="checkbox">Checkbox</option>
                          </select>
                        </div>
                        <div className="col-span-4">
                          {renderFieldInput(field)}
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          <button
                            onClick={() => removeCustomField(field.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!contentData.projectName}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Content
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Content Creation */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Main Content</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  value={mainContent}
                  onChange={(e) => setMainContent(e.target.value)}
                  rows={15}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Enter your project content, specifications, or requirements here..."
                />
                <p className="text-xs text-gray-500 mt-1">Markdown supported</p>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next: Assets
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Asset Upload */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Upload Assets (Optional)</h2>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label htmlFor="files-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Any file type supported</p>
                  <input
                    id="files-upload"
                    type="file"
                    onChange={handleFilesUpload}
                    multiple
                    className="hidden"
                  />
                </div>

                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  Preview
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Preview & Submit */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Preview & Submit</h2>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="bg-white rounded-lg p-6">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">{contentData.projectName}</h1>

                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    {contentData.projectType && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{contentData.projectType}</span>}
                    <span className={`px-2 py-1 rounded ${
                      contentData.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                      contentData.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                      contentData.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {contentData.priority.charAt(0).toUpperCase() + contentData.priority.slice(1)} Priority
                    </span>
                    {contentData.dueDate && <span>Due: {new Date(contentData.dueDate).toLocaleDateString()}</span>}
                  </div>

                  <p className="text-gray-700 mb-4">{contentData.description}</p>

                  {customFields.length > 0 && (
                    <div className="border-t pt-4 mb-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Custom Fields</h3>
                      <div className="space-y-2">
                        {customFields.map(field => (
                          <div key={field.id} className="flex justify-between text-sm">
                            <span className="text-gray-600">{field.name}:</span>
                            <span className="font-medium">{field.value?.toString() || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {mainContent && (
                    <div className="border-t pt-4 mb-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Content</h3>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{mainContent.substring(0, 300)}...</p>
                    </div>
                  )}

                  {files.length > 0 && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Attached Files ({files.length})</h3>
                      <div className="space-y-1">
                        {files.map((file, i) => (
                          <div key={i} className="text-sm text-gray-600">{file.name}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Workflow Builder (Advanced) */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Workflow Steps (Advanced)</h3>
              <button
                onClick={addWorkflowStep}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Step
              </button>
            </div>

            <div className="space-y-2">
              {workflowSteps.map((step) => (
                <div key={step.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {step.order}
                  </span>
                  <input
                    type="text"
                    value={step.name}
                    onChange={(e) => updateWorkflowStep(step.id, { name: e.target.value })}
                    className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                  />
                  <select
                    value={step.type}
                    onChange={(e) => updateWorkflowStep(step.id, { type: e.target.value as any })}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="metadata">Metadata</option>
                    <option value="content">Content</option>
                    <option value="assets">Assets</option>
                    <option value="preview">Preview</option>
                    <option value="approval">Approval</option>
                    <option value="custom">Custom</option>
                  </select>
                  {workflowSteps.length > 1 && (
                    <button
                      onClick={() => removeWorkflowStep(step.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
